import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@mui/material";
import { Plus, Search, Trash2, LayoutGrid, Layers } from "lucide-react";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { DeleteModal } from "../../../../components";
import { useStore } from "../../../../helpers";
import type { CanvasListItem } from "../../tars-hooks/useCanvasList";
import { FACET_UPDATE_DEFAULT_VERSION, CANVAS_LIST_SCROLL_MAX_HEIGHT_PX } from "../../tars-constants";
import { useDeleteFacet, useUpdateFacet } from "../../tars-hooks";
import { useDeleteCanvas } from "../../tars-hooks/useDeleteCanvas";
import { resolveServerFacetId, resolvedEtlJsonForPersist } from "./facet-workspace-update-helpers";
import type { SelectedCanvas } from "../../../../stores/tars/tars-store";
import { useTrieSearch } from "../../../../custom-hooks";
import { safe_get } from "../../../../report-visuals";
import FacetListTypeIcon from "./FacetListTypeIcon";
import CanvasListModalRow from "./CanvasListModalRow";

const canvasButtonSx = {
    textTransform: "none" as const,
    fontSize: 14,
    backgroundColor: "#22C55E",
    "&:hover": { backgroundColor: "#16A34A" },
};

interface CanvasPaneProps {
    canvases: CanvasListItem[];
    selectedCanvas: SelectedCanvas | null;
    onSelectCanvas: (canvas: SelectedCanvas) => void;
    onAddNewCanvas: () => void;
}

function CanvasPaneInner({ canvases, selectedCanvas, onSelectCanvas, onAddNewCanvas }: CanvasPaneProps) {
    const { notebookId } = useParams<{ notebookId: string }>();
    const notebookIdNum = Number(notebookId);
    const [canvasPendingDelete, setCanvasPendingDelete] = useState<CanvasListItem | null>(null);
    const [editingFacetId, setEditingFacetId] = useState<string | null>(null);
    const [editingFacetName, setEditingFacetName] = useState("");
    const [facetSearchQuery, setFacetSearchQuery] = useState("");
    const [facetPendingDelete, setFacetPendingDelete] = useState<{
        id: string;
        label: string;
    } | null>(null);
    const queryClient = useQueryClient();
    const { tarsAnalyticsStore } = useStore();
    const deleteFacet = useDeleteFacet();
    const updateFacet = useUpdateFacet();
    const deleteCanvas = useDeleteCanvas();
    const { facets, facetCount } = tarsAnalyticsStore;

    const facetTrieItems = useMemo(
        () =>
            facets.map((f) => ({
                ...f,
                facetSearchLabel: String(safe_get(f, "more.name", f.name) ?? ""),
            })),
        [facets]
    );

    const effectiveFacetSearchQuery = facetSearchQuery.trim().length < 2 ? "" : facetSearchQuery.trim();

    const filteredFacets = useTrieSearch({
        data: facetTrieItems,
        searchQuery: effectiveFacetSearchQuery,
        searchTearms: ["facetSearchLabel"],
        indexField: "id",
    });

    const handleFacetDoubleClick = (facetId: string, currentName: string) => {
        setEditingFacetId(facetId);
        setEditingFacetName(currentName);
    };

    const handleSaveRename = (facetId: string) => {
        const nextName = editingFacetName.trim();
        const facet = facets.find((f) => f.id === facetId);
        setEditingFacetId(null);
        setEditingFacetName("");
        if (!nextName || !facet) return;

        const apiFacetId = resolveServerFacetId(facet);
        if (apiFacetId == null) {
            toast.error(
                "Cannot rename until this facet is synced with the server (wait a moment and try again)."
            );
            return;
        }

        const prevName = facet.name;
        const prevMore = {
            ...((facet.more as Record<string, unknown> | undefined) ?? {}),
        };
        const nextMore = { ...prevMore, name: nextName };
        tarsAnalyticsStore.updateFacet(facetId, {
            name: nextName,
            more: nextMore,
        });

        updateFacet.mutate(
            {
                facet_id: apiFacetId,
                ...(Number.isFinite(notebookIdNum) && notebookIdNum > 0
                    ? { notebook_id: notebookIdNum }
                    : {}),
                etl_json: resolvedEtlJsonForPersist(facet),
                facet_result: (facet.facet_result as Record<string, unknown> | undefined) ?? {},
                version:
                    typeof facet.version === "number" && Number.isFinite(facet.version)
                        ? facet.version
                        : FACET_UPDATE_DEFAULT_VERSION,
                more: nextMore,
            },
            {
                onSuccess: () => {
                    void queryClient.invalidateQueries({
                        queryKey: ["tars", "facets", "list"],
                    });
                },
                onError: () => {
                    tarsAnalyticsStore.updateFacet(facetId, {
                        name: prevName,
                        more: prevMore,
                    });
                },
            }
        );
    };

    const performDeleteFacet = (facetId: string) => {
        const facet = facets.find((f) => f.id === facetId);
        const apiFacetId = typeof facet?.facet_id === "number" ? facet.facet_id : Number(facet?.id ?? NaN);

        if (!Number.isFinite(apiFacetId) || apiFacetId <= 0) {
            tarsAnalyticsStore.removeFacet(facetId);
            return;
        }

        deleteFacet.mutate(
            {
                facet_id: apiFacetId,
                ...(Number.isFinite(notebookIdNum) && notebookIdNum > 0
                    ? { notebook_id: notebookIdNum }
                    : {}),
            },
            {
                onSuccess: () => {
                    tarsAnalyticsStore.removeFacet(facetId);
                },
            }
        );
    };

    const handleConfirmDeleteFacet = (_e: unknown, facetId: string) => {
        if (!facetId) return;
        setFacetPendingDelete(null);
        performDeleteFacet(facetId);
    };

    const handleSelectCanvas = (canvas: CanvasListItem) => {
        onSelectCanvas({ id: canvas.id, name: canvas.name });
    };

    const handleConfirmDeleteCanvas = (_e: unknown, _id: string) => {
        if (!canvasPendingDelete) return;
        const target = canvasPendingDelete;
        deleteCanvas.mutate(
            {
                canvas_id: target.id,
                ...(Number.isFinite(notebookIdNum) && notebookIdNum > 0
                    ? { notebook_id: notebookIdNum }
                    : {}),
                type: target.type,
                name: target.name,
            },
            {
                onSuccess: () => setCanvasPendingDelete(null),
            }
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent, facetId: string) => {
        if (e.key === "Enter") {
            handleSaveRename(facetId);
        } else if (e.key === "Escape") {
            setEditingFacetId(null);
            setEditingFacetName("");
        }
    };

    return (
        <>
            <aside
                className="flex min-h-0 shrink-0 flex-col border-l border-gray-200 bg-[#F9FAFB]"
                style={{
                    width: tarsAnalyticsStore.canvasPaneWidth,
                    minWidth: 200,
                }}
                aria-label="Canvas and facets"
            >
                <header className="flex shrink-0 items-center justify-start border-b border-gray-200 bg-white px-4 py-3">
                    <Button
                        variant="contained"
                        size="medium"
                        startIcon={<Plus size={18} strokeWidth={2.5} />}
                        onClick={onAddNewCanvas}
                        sx={canvasButtonSx}
                    >
                        Create Canvas
                    </Button>
                </header>

                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <section
                        className="flex min-h-0 shrink-0 flex-col border-b-2 border-emerald-200/80 bg-gradient-to-b from-emerald-50/90 to-emerald-50/30"
                        aria-label="Canvas list"
                    >
                        <div className="flex shrink-0 items-center justify-between border-b border-emerald-100/80 bg-emerald-50/70 px-4 py-2.5">
                            <h2 className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-emerald-800">
                                <LayoutGrid size={14} strokeWidth={2.25} aria-hidden />
                                Canvases
                            </h2>
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                {canvases.length}
                            </span>
                        </div>
                        <nav
                            className="min-h-0 overflow-y-auto px-2 py-2"
                            style={{ maxHeight: CANVAS_LIST_SCROLL_MAX_HEIGHT_PX }}
                        >
                            <ul className="m-0 flex list-none flex-col gap-1 p-0">
                                {canvases.map((canvas) => (
                                    <li key={canvas.id}>
                                        <CanvasListModalRow
                                            canvas={canvas}
                                            isSelected={selectedCanvas?.id === canvas.id}
                                            isDeleting={
                                                deleteCanvas.isLoading &&
                                                deleteCanvas.variables?.canvas_id === canvas.id
                                            }
                                            onSelect={() => handleSelectCanvas(canvas)}
                                            onDelete={() => setCanvasPendingDelete(canvas)}
                                        />
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </section>

                    <section className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-100/90">
                        {selectedCanvas ? (
                            <>
                                <div className="shrink-0 border-b border-slate-200 bg-white px-4 pb-3 pt-3">
                                    <div
                                        className="flex items-center justify-between"
                                        aria-labelledby="canvas-pane-facets-title"
                                    >
                                        <h2
                                            id="canvas-pane-facets-title"
                                            className="flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-slate-700"
                                        >
                                            <Layers size={14} strokeWidth={2.25} aria-hidden />
                                            Facets
                                        </h2>
                                        <span className="rounded-full bg-slate-200/80 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                                            {effectiveFacetSearchQuery
                                                ? `${filteredFacets.length}/${facetCount}`
                                                : facetCount}
                                        </span>
                                    </div>
                                    <search className="relative mt-2.5 block">
                                        <Search
                                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                                            strokeWidth={2}
                                            aria-hidden
                                        />
                                        <input
                                            type="search"
                                            placeholder="Filter facets by name"
                                            value={facetSearchQuery}
                                            onChange={(e) => setFacetSearchQuery(e.target.value)}
                                            className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-[13px] text-gray-800 outline-none placeholder:text-slate-400 focus:border-slate-400 focus:bg-white"
                                            aria-label="Filter facets by name"
                                        />
                                    </search>
                                </div>
                                {facets.length > 0 ? (
                                    <nav
                                        className="mx-2 mb-2 mt-2 min-h-0 flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-sm"
                                        aria-label="Facets"
                                    >
                                        {effectiveFacetSearchQuery && filteredFacets.length === 0 ? (
                                            <p
                                                className="px-4 py-6 text-center text-sm text-gray-500"
                                                role="status"
                                            >
                                                No facets match this filter.
                                            </p>
                                        ) : null}
                                        <ul className="m-0 list-none p-0">
                                            {filteredFacets.map((facet) => {
                                                const facetLabel = String(
                                                    safe_get(facet, "more.name", facet.name) ?? ""
                                                );
                                                return (
                                                    <li
                                                        key={facet.id}
                                                        className={`group mx-1 flex cursor-pointer items-center justify-between gap-2 rounded-md px-3 py-2.5 text-[14px] transition-colors first:mt-1 last:mb-1 ${
                                                            tarsAnalyticsStore.selectedFacetId === facet.id
                                                                ? "bg-slate-900 text-white shadow-sm"
                                                                : "text-slate-800 hover:bg-slate-50"
                                                        }`}
                                                        onClick={() =>
                                                            tarsAnalyticsStore.setSelectedFacetId(facet.id)
                                                        }
                                                        onDoubleClick={() =>
                                                            handleFacetDoubleClick(facet.id, facetLabel)
                                                        }
                                                    >
                                                        <div className="flex min-w-0 flex-1 items-center gap-2">
                                                            <FacetListTypeIcon
                                                                facet={facet}
                                                                selected={
                                                                    tarsAnalyticsStore.selectedFacetId ===
                                                                    facet.id
                                                                }
                                                            />
                                                            {editingFacetId === facet.id ? (
                                                                <input
                                                                    type="text"
                                                                    autoFocus
                                                                    value={editingFacetName}
                                                                    onChange={(e) =>
                                                                        setEditingFacetName(e.target.value)
                                                                    }
                                                                    onBlur={() => handleSaveRename(facet.id)}
                                                                    onKeyDown={(e) => handleKeyDown(e, facet.id)}
                                                                    className={`flex-1 rounded border px-2 py-1 outline-none ${
                                                                        tarsAnalyticsStore.selectedFacetId ===
                                                                        facet.id
                                                                            ? "border-white bg-gray-800 text-white"
                                                                            : "border-gray-300 bg-white text-gray-800"
                                                                    }`}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                />
                                                            ) : (
                                                                <span className="truncate">{facetLabel}</span>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setFacetPendingDelete({
                                                                    id: facet.id,
                                                                    label: facetLabel,
                                                                });
                                                            }}
                                                            className={`shrink-0 rounded p-1 opacity-0 transition-opacity group-hover:opacity-100 ${
                                                                tarsAnalyticsStore.selectedFacetId === facet.id
                                                                    ? "text-white hover:bg-slate-700"
                                                                    : "text-slate-400 hover:bg-slate-100"
                                                            }`}
                                                            title="Delete facet"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </nav>
                                ) : (
                                    <div
                                        className="mx-2 mb-2 mt-2 flex flex-1 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6"
                                        aria-label="No facets"
                                    >
                                        <p className="text-center text-sm text-gray-500">
                                            No facets yet. Add or generate facets in the analytics view.
                                        </p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div
                                className="mx-2 mb-2 mt-2 flex flex-1 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6"
                                aria-label="No canvas selected"
                            >
                                <p className="text-center text-sm text-gray-500">
                                    Select a canvas to view its facets.
                                </p>
                            </div>
                        )}
                    </section>
                </div>
            </aside>

            <DeleteModal
                isOpen={!!canvasPendingDelete}
                id={canvasPendingDelete ? String(canvasPendingDelete.id) : undefined}
                desc={canvasPendingDelete ? `Delete "${canvasPendingDelete.name}"?` : ""}
                onDeleteClick={handleConfirmDeleteCanvas}
                onCancelClick={() => setCanvasPendingDelete(null)}
            />

            <DeleteModal
                isOpen={!!facetPendingDelete}
                id={facetPendingDelete?.id}
                desc={
                    facetPendingDelete
                        ? `Delete facet "${facetPendingDelete.label}"? This cannot be undone.`
                        : ""
                }
                onDeleteClick={handleConfirmDeleteFacet}
                onCancelClick={() => setFacetPendingDelete(null)}
            />
        </>
    );
}

export default observer(CanvasPaneInner);
